import React from 'react';
import { Clock, Search, X } from 'lucide-react';

import { COUPON_TYPES } from './couponAdapter';
import { STATUS_ORDER, STATUS_STYLES } from './couponStatus';

const TYPE_FILTERS = ['All', ...COUPON_TYPES];

/**
 * Search and the status/type chips above the table.
 *
 * The counts on each status chip are computed over every coupon the camp has, not the current
 * page — the whole list is in memory, so a chip reading "Expired 12" is the real number and
 * clicking it shows all twelve.
 */
const CouponFilterBar = ({
    filters,
    statusCounts,
    expiringCount,
    filtersApplied,
    onQueryChange,
    onStatusChange,
    onTypeChange,
    onExpiringChange,
    onClear,
}) => {
    const chipClass = selected =>
        `cpn-inline-flex cpn-items-center cpn-gap-1.5 cpn-rounded-full cpn-border cpn-px-3 cpn-py-1.5 cpn-text-xs cpn-font-semibold cpn-transition ${
            selected
                ? 'cpn-border-brand-600 cpn-bg-brand-700 cpn-text-white'
                : 'cpn-border-neutral-200 cpn-bg-white cpn-text-neutral-600 hover:cpn-border-brand-300 hover:cpn-text-brand-700'
        }`;

    // A label and its chips as one wrapping row. Grouping them means a wrap can never orphan
    // "Type" onto the line above its own buttons, which is what a single row over both groups did.
    //
    // Rows are left-aligned, not right-aligned, for two reasons: it puts TYPE directly under
    // STATUS, and it means the Clear button appearing extends the row rightwards instead of
    // pushing every chip in it to the left. The label's min-width lines the two rows' first chips
    // up with each other despite "STATUS" being the wider word.
    const group = (label, children) => (
        <div className="cpn-flex cpn-flex-wrap cpn-items-center cpn-gap-2">
            <span className="cpn-min-w-[3.75rem] cpn-text-xs cpn-font-semibold cpn-uppercase cpn-tracking-wider cpn-text-neutral-400">
                {label}
            </span>
            {children}
        </div>
    );

    return (
        <div className="cpn-flex cpn-flex-col cpn-gap-4 cpn-border-b cpn-border-neutral-200 cpn-px-4 cpn-py-4 sm:cpn-px-6 xl:cpn-flex-row xl:cpn-items-start xl:cpn-justify-between">
            <div className="cpn-relative cpn-w-full cpn-max-w-md xl:cpn-shrink-0">
                <label className="cpn-sr-only" htmlFor="coupon-search">
                    Search coupon codes
                </label>
                <Search
                    className="cpn-pointer-events-none cpn-absolute cpn-left-3.5 cpn-top-1/2 cpn-h-4 cpn-w-4 -cpn-translate-y-1/2 cpn-text-neutral-400"
                    aria-hidden="true"
                />
                <input
                    id="coupon-search"
                    type="search"
                    value={filters.query}
                    onChange={event => onQueryChange(event.target.value)}
                    placeholder="Search by coupon name or code"
                    className="cpn-h-11 cpn-w-full cpn-rounded-xl cpn-border cpn-border-neutral-200 cpn-bg-neutral-50 cpn-pl-10 cpn-pr-10 cpn-text-sm cpn-text-neutral-900 placeholder:cpn-text-neutral-400 cpn-transition focus:cpn-border-brand-500 focus:cpn-bg-white focus:cpn-outline-none focus:cpn-ring-4 focus:cpn-ring-brand-100"
                />
                {filters.query && (
                    <button
                        type="button"
                        onClick={() => onQueryChange('')}
                        aria-label="Clear search"
                        className="cpn-absolute cpn-right-3 cpn-top-1/2 -cpn-translate-y-1/2 cpn-rounded-md cpn-p-1 cpn-text-neutral-400 cpn-transition hover:cpn-bg-neutral-100 hover:cpn-text-neutral-700"
                    >
                        <X className="cpn-h-4 cpn-w-4" aria-hidden="true" />
                    </button>
                )}
            </div>

            {/* Status over Type, as one right-hand column beside the search — the original
                single-band layout. Two short rows rather than one long wrapping one, which is what
                left "Amount" stranded on a line of its own. */}
            <div className="cpn-flex cpn-flex-col cpn-gap-2">
                {group(
                    'Status',
                    <>
                        <button
                            type="button"
                            className={chipClass(filters.status === 'All')}
                            onClick={() => onStatusChange('All')}
                        >
                            All
                        </button>

                        {STATUS_ORDER.map(status => (
                            <button
                                key={status}
                                type="button"
                                className={chipClass(filters.status === status)}
                                onClick={() => onStatusChange(status)}
                            >
                                <span
                                    className={`cpn-h-1.5 cpn-w-1.5 cpn-rounded-full ${
                                        filters.status === status
                                            ? 'cpn-bg-white'
                                            : STATUS_STYLES[status].dot
                                    }`}
                                    aria-hidden="true"
                                />
                                {status}
                                <span
                                    className={
                                        filters.status === status
                                            ? 'cpn-text-brand-100'
                                            : 'cpn-text-neutral-400'
                                    }
                                >
                                    {statusCounts[status] || 0}
                                </span>
                            </button>
                        ))}

                        {expiringCount > 0 && (
                            <button
                                type="button"
                                aria-pressed={filters.expiringOnly}
                                className={`cpn-inline-flex cpn-items-center cpn-gap-1.5 cpn-rounded-full cpn-border cpn-px-3 cpn-py-1.5 cpn-text-xs cpn-font-semibold cpn-transition ${
                                    filters.expiringOnly
                                        ? 'cpn-border-orange-600 cpn-bg-orange-600 cpn-text-white'
                                        : 'cpn-border-orange-200 cpn-bg-orange-50 cpn-text-orange-700 hover:cpn-border-orange-400'
                                }`}
                                onClick={() => onExpiringChange(!filters.expiringOnly)}
                            >
                                <Clock className="cpn-h-3 cpn-w-3" aria-hidden="true" />
                                Expiring soon
                                <span
                                    className={
                                        filters.expiringOnly
                                            ? 'cpn-text-orange-100'
                                            : 'cpn-text-orange-500'
                                    }
                                >
                                    {expiringCount}
                                </span>
                            </button>
                        )}
                    </>
                )}

                {group(
                    'Type',
                    <>
                        {TYPE_FILTERS.map(type => (
                            <button
                                key={type}
                                type="button"
                                className={chipClass(filters.type === type)}
                                onClick={() => onTypeChange(type)}
                            >
                                {type}
                            </button>
                        ))}

                        {filtersApplied && (
                            <button
                                type="button"
                                onClick={onClear}
                                className="cpn-ml-1 cpn-inline-flex cpn-items-center cpn-gap-1 cpn-rounded-full cpn-px-2 cpn-py-1.5 cpn-text-xs cpn-font-semibold cpn-text-brand-700 cpn-transition hover:cpn-bg-brand-50"
                            >
                                <X className="cpn-h-3 cpn-w-3" aria-hidden="true" />
                                Clear
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CouponFilterBar;
